import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Badge, PageLoading, Select, TextInput, Toggle } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { LeadStage, LeadStatus, LeadStatusCategory } from '../../types';

const CAT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  open: 'warning', won: 'success', lost: 'danger',
};
const CAT_LABEL: Record<LeadStatusCategory, string> = { open: 'Open', won: 'Won', lost: 'Lost' };

export default function LeadPipelinePage() {
  const toast = useToast();
  const [stages, setStages] = useState<LeadStage[] | null>(null);
  const [statuses, setStatuses] = useState<LeadStatus[] | null>(null);
  const [editingStage, setEditingStage] = useState<LeadStage | 'new' | null>(null);
  const [editingStatus, setEditingStatus] = useState<LeadStatus | 'new' | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, ss] = await Promise.all([
        api.get<LeadStage[]>('/leads/stages'),
        api.get<LeadStatus[]>('/leads/statuses'),
      ]);
      setStages(st); setStatuses(ss);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the pipeline.');
      setStages([]); setStatuses([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function move(index: number, dir: -1 | 1) {
    if (!stages) return;
    const next = [...stages];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j]!, next[index]!];
    setStages(next);
    setBusy(true);
    try {
      await api.post('/leads/stages/reorder', { ids: next.map((s) => s.id) });
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not reorder.');
      void load();
    } finally { setBusy(false); }
  }

  async function toggleActive(kind: 'stage' | 'status', id: string, isActive: boolean) {
    try {
      await api.patch(`/leads/${kind === 'stage' ? 'stages' : 'statuses'}/${id}`, { isActive });
      void load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update.');
    }
  }

  return (
    <>
      <PageHeader title="Pipeline & statuses"
        subtitle="The stages a lead moves through, and the outcomes you track." />

      <div className="page col gap-16">
        {/* Stages */}
        <div className="card">
          <div className="between" style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <strong>Pipeline stages</strong>
              <div className="small faint">Drag order with the arrows — first is where new leads start.</div>
            </div>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditingStage('new')}>
              <Icon name="plus" size={15} /> Add stage
            </button>
          </div>
          {stages === null ? <div className="card-pad"><PageLoading rows={3} /></div> : (
            <table className="data">
              <tbody>
                {stages.map((s, i) => (
                  <tr key={s.id}>
                    <td style={{ width: 70 }}>
                      <div className="row gap-4">
                        <button type="button" className="icon-btn" disabled={busy || i === 0} title="Up"
                          onClick={() => void move(i, -1)}><Icon name="chevron-down" size={14} className="nav-caret open" /></button>
                        <button type="button" className="icon-btn" disabled={busy || i === stages.length - 1} title="Down"
                          onClick={() => void move(i, 1)}><Icon name="chevron-down" size={14} /></button>
                      </div>
                    </td>
                    <td className="primary">
                      <span className="row gap-8" style={{ alignItems: 'center' }}>
                        {s.label}{!s.isActive ? <Badge tone="neutral">Hidden</Badge> : null}
                      </span>
                    </td>
                    <td className="num faint">{s.leadCount ?? 0} leads</td>
                    <td style={{ width: 90 }}>
                      <Toggle checked={s.isActive} label="" onChange={(v) => void toggleActive('stage', s.id, v)} />
                    </td>
                    <td style={{ width: 44 }}>
                      <button type="button" className="icon-btn" title="Edit" onClick={() => setEditingStage(s)}>
                        <Icon name="edit" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Statuses */}
        <div className="card">
          <div className="between" style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <strong>Statuses</strong>
              <div className="small faint">Each is an Open, Won or Lost outcome — that drives the win-rate.</div>
            </div>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditingStatus('new')}>
              <Icon name="plus" size={15} /> Add status
            </button>
          </div>
          {statuses === null ? <div className="card-pad"><PageLoading rows={3} /></div> : (
            <table className="data">
              <tbody>
                {statuses.map((s) => (
                  <tr key={s.id}>
                    <td className="primary">
                      <span className="row gap-8" style={{ alignItems: 'center' }}>
                        {s.label}{!s.isActive ? <Badge tone="neutral">Hidden</Badge> : null}
                      </span>
                    </td>
                    <td><Badge tone={CAT_TONE[s.category]}>{CAT_LABEL[s.category]}</Badge></td>
                    <td className="num faint">{s.leadCount ?? 0} leads</td>
                    <td style={{ width: 90 }}>
                      <Toggle checked={s.isActive} label="" onChange={(v) => void toggleActive('status', s.id, v)} />
                    </td>
                    <td style={{ width: 44 }}>
                      <button type="button" className="icon-btn" title="Edit" onClick={() => setEditingStatus(s)}>
                        <Icon name="edit" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editingStage ? (
        <StageDialog stage={editingStage === 'new' ? null : editingStage}
          onClose={() => setEditingStage(null)} onSaved={() => { setEditingStage(null); void load(); }} />
      ) : null}
      {editingStatus ? (
        <StatusDialog status={editingStatus === 'new' ? null : editingStatus}
          onClose={() => setEditingStatus(null)} onSaved={() => { setEditingStatus(null); void load(); }} />
      ) : null}
    </>
  );
}

function StageDialog({ stage, onClose, onSaved }: {
  stage: LeadStage | null; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [label, setLabel] = useState(stage?.label ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      if (stage) await api.patch(`/leads/stages/${stage.id}`, { label: label.trim() });
      else await api.post('/leads/stages', { label: label.trim() });
      toast.success(stage ? 'Saved.' : `${label.trim()} added.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save.');
    } finally { setBusy(false); }
  }
  async function remove() {
    if (!stage) return;
    setBusy(true);
    try {
      const r = await api.del<{ deactivated: boolean; leadsUsing?: number }>(`/leads/stages/${stage.id}`);
      toast.success(r.deactivated ? `${stage.label} is used by ${r.leadsUsing} lead(s), so it was hidden.` : `${stage.label} removed.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove.');
    } finally { setBusy(false); }
  }

  return (
    <Modal title={stage ? stage.label : 'Add a stage'} onClose={onClose} footer={(
      <>
        {stage ? <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>Remove</button> : null}
        <span className="grow" />
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy || !label.trim()} onClick={() => void save()}>
          {stage ? 'Save' : 'Add'}
        </button>
      </>
    )}>
      <TextInput label="Stage name" value={label} placeholder="Qualified" autoFocus
        onChange={(e) => setLabel(e.target.value)} />
    </Modal>
  );
}

function StatusDialog({ status, onClose, onSaved }: {
  status: LeadStatus | null; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [label, setLabel] = useState(status?.label ?? '');
  const [category, setCategory] = useState<LeadStatusCategory>(status?.category ?? 'open');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      if (status) await api.patch(`/leads/statuses/${status.id}`, { label: label.trim(), category });
      else await api.post('/leads/statuses', { label: label.trim(), category });
      toast.success(status ? 'Saved.' : `${label.trim()} added.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save.');
    } finally { setBusy(false); }
  }
  async function remove() {
    if (!status) return;
    setBusy(true);
    try {
      const r = await api.del<{ deactivated: boolean; leadsUsing?: number }>(`/leads/statuses/${status.id}`);
      toast.success(r.deactivated ? `${status.label} is used by ${r.leadsUsing} lead(s), so it was hidden.` : `${status.label} removed.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove.');
    } finally { setBusy(false); }
  }

  return (
    <Modal title={status ? status.label : 'Add a status'} onClose={onClose} footer={(
      <>
        {status ? <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>Remove</button> : null}
        <span className="grow" />
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy || !label.trim()} onClick={() => void save()}>
          {status ? 'Save' : 'Add'}
        </button>
      </>
    )}>
      <div className="col gap-12">
        <TextInput label="Status name" value={label} placeholder="Proposal sent" autoFocus
          onChange={(e) => setLabel(e.target.value)} />
        <Select label="Outcome" value={category} hint="Won and Lost close the lead; the win-rate is won ÷ (won + lost)."
          onChange={(e) => setCategory(e.target.value as LeadStatusCategory)}>
          <option value="open">Open — still in play</option>
          <option value="won">Won — became a customer</option>
          <option value="lost">Lost — did not convert</option>
        </Select>
      </div>
    </Modal>
  );
}
